import { useState } from "react";
import { useTranslation } from "react-i18next";

import { useSuppressPhone } from "@/api/queries";

/**
 * "No me llames", in one click and one confirmation (.docs/06 §4, item 8).
 *
 * Two steps rather than one, and no `window.confirm`: the first click is next
 * to a dial button, and a misfire records a client as un-callable — a state the
 * SPA cannot undo, on purpose. The inline confirm keeps the whole thing inside
 * the row it belongs to.
 */
export function SuppressButton({
  phone,
  note,
  label,
  className = "btn btn-secondary px-2 py-1 text-xs",
}: {
  phone: string;
  note?: string;
  /** The words and the look of the first button, where a screen sets its own. */
  label?: string;
  className?: string;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const suppress = useSuppressPhone();

  if (suppress.isSuccess) {
    return <span className="text-xs text-brand-700">{t("suppress.saved")}</span>;
  }

  if (!confirming) {
    return (
      <button
        type="button"
        onClick={() => setConfirming(true)}
        title={t("suppress.hint")}
        className={className}
      >
        {label ?? t("suppress.action")}
      </button>
    );
  }

  return (
    <span className="flex items-center gap-1">
      <button
        type="button"
        onClick={() => suppress.mutate({ phone, ...(note ? { note } : {}) })}
        disabled={suppress.isPending}
        className="btn btn-primary px-2 py-1 text-xs"
      >
        {t("suppress.confirm")}
      </button>
      <button
        type="button"
        onClick={() => setConfirming(false)}
        className="btn btn-ghost px-2 py-1 text-xs"
      >
        {t("suppress.cancel")}
      </button>
    </span>
  );
}
